import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { useAuth } from "@/providers/auth";
import { useDomainServices } from "@/providers/DomainServices";
import { useNavigation } from "@/providers/navigation/NavigationContext";
import { useCreateBoard } from "@/shared/domain/boards/hooks";
import { useCreateCollection } from "@/shared/domain/collections/hooks/useCreateCollection";
import { useUpdateCollection } from "@/shared/domain/collections/hooks/useUpdateCollection";
import { getQueryKey as searchTablePreferencesQueryKey } from "@/shared/domain/config/hooks/useSearchTablePreferences";
import { searchTablePreferenceKeyFor } from "@/shared/domain/config/searchTablePreferenceKeys";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { useOrgEventModel } from "@/shared/domain/orgs/hooks";
import { usePublishToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  RESOURCE_TABLE_ACTION_TOPIC,
  WORKSPACE_ACTION_TOPIC,
  type ResourceTableActionPayload,
  type WorkspaceActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { ViewingContextService } from "@/shared/services/ViewingContextService/ViewingContextService";

import { AgentThreadRecord, AgentThreadStatus } from "../AgentThreadRecord";
import { isClientToolName } from "../clientTools";
import type { SavedToolPermissions } from "../clientTools/approvalModes";
import {
  ClientToolBatchRunner,
  type PendingToolApproval,
} from "../clientTools/ClientToolBatchRunner";
import type { ClientToolDeps } from "../clientTools/ClientTools";
import { claimToolUses } from "../clientTools/toolUseClaims";
import { findPendingClientToolUses } from "../pendingClientToolUses";
import { requestsApplyToOf } from "../requestsApplyTo";

import { useReleasedClientTools } from "./useReleasedClientTools";

export type { PendingToolApproval };

interface ClientToolExecutionOptions {
  /**
   * Stand down: watch the thread but execute nothing.
   *
   * Set when another mounted copy of the conversation owns execution for this
   * thread: the thread detail page once it has handed the thread to the chat
   * sidebar. {@link claimToolUses} already keeps a `tool_use_id` from running
   * twice; this decides which copy wins, so the batch and any approval it
   * raises land on the conversation view that survives the navigation.
   */
  suspended?: boolean;
}

/**
 * Watches an AgentThreadRecord and runs the client tool calls it waits on,
 * asking for approval per each tool's approval mode and refusing a call to a
 * tool not released to the person.
 *
 * Delegates batch logic (triage, queue, result accumulation) to
 * {@link ClientToolBatchRunner}. This hook is a thin adapter that bridges
 * React context to the runner's lifecycle methods.
 */
export function useClientToolExecution(
  agentThread: AgentThreadRecord | undefined,
  orgId: string | undefined,
  autoEnabled: boolean,
  savedPermissions: SavedToolPermissions,
  { suspended = false }: ClientToolExecutionOptions = {},
) {
  const {
    agentThreadService,
    agentService,
    boardService,
    skillService,
    events: eventService,
    topics: topicService,
    files: fileService,
    layoutService,
    collections: collectionService,
    viewService,
    configService,
    customFieldService,
    metricsService,
    queryService,
    sessionService,
  } = useDomainServices();
  const { currentUser } = useAuth();
  const eventModel = useOrgEventModel(orgId);
  const { goto } = useNavigation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { resolve: resolveClientTool, flagsSettled } = useReleasedClientTools();
  const { mutateAsync: createBoard } = useCreateBoard();
  const { mutateAsync: createCollection } = useCreateCollection();
  const { mutateAsync: updateCollection } = useUpdateCollection();
  const publishWorkspaceAction =
    usePublishToAiAgentThreadPubSubBus<WorkspaceActionPayload>(
      WORKSPACE_ACTION_TOPIC,
    );
  const publishResourceTableAction =
    usePublishToAiAgentThreadPubSubBus<ResourceTableActionPayload>(
      RESOURCE_TABLE_ACTION_TOPIC,
    );

  const lastExecutedRef = useRef<string | null>(null);
  const [pendingApproval, setPendingApproval] =
    useState<PendingToolApproval | null>(null);

  // Deps are rebuilt each render and stored in a ref so the runner always reads
  // current values (e.g. isOnVisualizerPage reflects the user's current route).
  // The single `deps` object feeds both the ref's initial value and `current`,
  // so the closures (isOnVisualizerPage, onEventCreated) have one definition
  // rather than two copies that can drift apart.
  const deps: ClientToolDeps = {
    goto,
    orgId,
    agentService,
    skillService,
    userId: currentUser?.user_id,
    eventService,
    eventModel,
    topicService,
    fileService,
    layoutService,
    collectionService,
    boardService,
    createBoard,
    // Read from the thread record, so it is the board the request this batch
    // answers was sent from, whatever page is on screen when a tool runs.
    requestsApplyTo: requestsApplyToOf(agentThread),
    customFieldService,
    metricsService,
    queryService,
    sessionService,
    queryClient,
    isOnVisualizerPage: () => location.pathname.startsWith("/visualize"),
    currentRoutePath: () => location.pathname,
    publishWorkspaceAction,
    onEventCreated: () => {
      if (!orgId) {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
      });
      // Every events list: the org's, and each dataset's, session's and
      // collection's.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
    },
    // The mutation hooks' own writes, so a tool cannot perform a collection
    // write without the invalidation that belongs to it. `collectionService`
    // above stays for reads.
    createCollection,
    updateCollection,
    viewService,
    resourceTableOnScreen: () =>
      ViewingContextService.getInstance().getViewingContext().resourceTable,
    applyViewToOpenTable: (view) =>
      publishResourceTableAction({ type: "apply_view", payload: { view } }),
    // The same write and invalidation `useUpdateSearchTablePreferences` does,
    // done by hand because that hook is per table and a tool serves all of
    // them. Only the one field, so the customer's column layout is not
    // collateral of setting a default.
    setDefaultView: async (target, viewId) => {
      if (!orgId) {
        throw new Error("Cannot set a default View with no current org");
      }
      const configKey = searchTablePreferenceKeyFor(target);
      await configService.updateTablePreferences(orgId, configKey, {
        default_view_id: viewId,
      });
      await queryClient.invalidateQueries({
        queryKey: searchTablePreferencesQueryKey(configKey),
      });
    },
  };
  const depsRef = useRef(deps);
  depsRef.current = deps;

  // Stable refs for callbacks so the runner can be created once.
  const agentThreadServiceRef = useRef(agentThreadService);
  agentThreadServiceRef.current = agentThreadService;
  const orgIdRef = useRef(orgId);
  orgIdRef.current = orgId;

  // One runner per mounted copy of the conversation, created once with stable
  // callbacks. StrictMode calls the initializer twice in dev; the constructor
  // has no side effects, so the discarded instance is harmless.
  const [runner] = useState(
    () =>
      new ClientToolBatchRunner({
        onPendingApprovalChanged: (pending) => setPendingApproval(pending),
        onBatchComplete: (threadId, results) => {
          const currentOrgId = orgIdRef.current;
          if (!currentOrgId || results.length === 0) {
            return;
          }
          agentThreadServiceRef.current
            .submitToolResults(
              threadId,
              { tool_results: results },
              { orgId: currentOrgId },
            )
            .catch((error: unknown) => {
              // Do NOT clear lastExecutedRef: running the batch again would
              // repeat its side effects (navigation, PubSub).
              ErrorMonitoringService.captureError(error);
            });
        },
      }),
  );

  // Answer whatever this executor still owes when the component that calls
  // this hook unmounts, typically because a tool it just auto-executed
  // navigated out from under it.
  //
  // Cleanup detaches one microtask later, and only if no setup ran in between.
  // In development, StrictMode runs this cleanup and the setup again in one
  // synchronous pass at mount, possibly with a batch already waiting on an
  // approval; detaching there would answer that approval as interrupted and
  // submit it, and a runner stays detached for good. An unmount has no setup
  // after it, so its detach goes through. The main effect's cleanup is not
  // used because it runs each time one of that effect's dependencies changes,
  // and detaching there would end a batch that is merely re-rendering.
  const effectMountedRef = useRef(false);
  useEffect(() => {
    effectMountedRef.current = true;
    return () => {
      effectMountedRef.current = false;
      queueMicrotask(() => {
        if (!effectMountedRef.current) {
          runner.detach();
        }
      });
    };
  }, [runner]);

  // A change to the automatic-run switch, to the saved permissions, or to the
  // tools released to the person, including their arrival, also decides the
  // calls already waiting for approval. This effect and the one below that
  // starts a batch decide nothing while either of these is loading:
  // - The saved permissions. When they are unavailable, both go ahead and
  //   every call asks (see `effectiveApprovalMode`).
  // - The person's flag states. When they fail to load, both go ahead and the
  //   board tools count as not released.
  useEffect(() => {
    if (suspended || savedPermissions.status === "loading" || !flagsSettled) {
      return;
    }
    void runner.updateExecutionSettings({
      autoEnabled,
      savedPermissions,
      deps: depsRef.current,
      resolveClientTool,
    });
  }, [
    autoEnabled,
    savedPermissions,
    suspended,
    runner,
    resolveClientTool,
    flagsSettled,
  ]);

  // Main effect: detect CLIENT_TOOL_TURN and delegate to runner
  useEffect(() => {
    if (
      !agentThread ||
      !orgId ||
      suspended ||
      savedPermissions.status === "loading" ||
      !flagsSettled
    ) {
      return;
    }
    if (agentThread.status !== AgentThreadStatus.CLIENT_TOOL_TURN) {
      return;
    }

    // Take the calls to every client tool, released to the person or not. The
    // runner answers a call to a tool not released to them with a refusal, so
    // every call in the batch gets a result, and once the results are
    // submitted the server can continue the turn.
    const pending = findPendingClientToolUses(agentThread, isClientToolName);
    if (pending.length === 0) {
      return;
    }

    // Deduplicate on the pending tool_use_ids, not on message count: the
    // server appends a `roboto` message mid-turn carrying partial server-tool
    // results, and a count-keyed key reads that as a new batch. The runner
    // ignores ids it has already handled, so a re-entry is harmless either
    // way; this just skips the call.
    const pendingIds = pending.map((toolUse) => toolUse.tool_use_id).join(",");
    const executionKey = `${agentThread.thread_id}:${pendingIds}`;
    if (lastExecutedRef.current === executionKey) {
      return;
    }

    // Claim before running: another mounted copy of this conversation derives
    // "pending" from its own fetch and would otherwise execute the same
    // tool_use, opening two panels and submitting two results for one id.
    // Claiming after the `lastExecutedRef` check keeps a re-entry by this
    // instance cheap, and recording the key only once we hold work leaves this
    // instance free to take a later batch when it loses the race.
    const claimed = new Set(
      claimToolUses(
        agentThread.thread_id,
        pending.map((toolUse) => toolUse.tool_use_id),
      ),
    );
    if (claimed.size === 0) {
      return;
    }

    lastExecutedRef.current = executionKey;

    void runner.startBatch({
      threadId: agentThread.thread_id,
      toolUses: pending.filter((toolUse) => claimed.has(toolUse.tool_use_id)),
      autoEnabled,
      savedPermissions,
      deps: depsRef.current,
      resolveClientTool,
    });
  }, [
    agentThread,
    orgId,
    autoEnabled,
    savedPermissions,
    suspended,
    runner,
    resolveClientTool,
    flagsSettled,
  ]);

  const handleApprove = useCallback(async () => {
    await runner.approve(depsRef.current);
  }, [runner]);

  const handleDecline = useCallback(() => {
    runner.decline();
  }, [runner]);

  return {
    pendingApproval,
    handleApprove,
    handleDecline,
  };
}
